import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { User, LogOut, Crown, ChevronDown, LogIn, Zap, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function UserMenu() {
  const { user, isAuthenticated, hasActiveSubscription, logout, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <div className="flex gap-2 items-center">
        <Button data-testid="nav-login-btn" variant="outline" size="sm"
          onClick={() => navigate('/login')}
          style={{ borderColor: theme.borderColor, color: theme.textPrimary }}>
          <LogIn className="w-4 h-4 mr-1" /> Sign In
        </Button>
        <Button data-testid="nav-register-btn" size="sm"
          onClick={() => navigate('/register')}
          className="text-white"
          style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}>
          <Zap className="w-4 h-4 mr-1" /> Free Trial
        </Button>
      </div>
    );
  }

  const isPro = hasActiveSubscription();
  const initial = (user?.name || user?.email || 'U')[0].toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button data-testid="user-menu-trigger"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all"
        style={{ background: `${theme.primary}10`, border: `1px solid ${theme.borderColor}` }}>
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
          style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}>
          {user?.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : initial}
        </div>
        <span className="text-sm font-medium max-w-[100px] truncate" style={{ color: theme.textPrimary }}>
          {user?.name || user?.email?.split('@')[0]}
        </span>
        {isPro && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
            style={{ background: `${theme.primary}20`, color: theme.primary }}>
            <Crown className="w-2.5 h-2.5 inline mr-0.5" />PRO
          </span>
        )}
        <ChevronDown className="w-3 h-3" style={{ color: theme.textSecondary }} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-xl overflow-hidden z-50 shadow-xl"
          style={{ background: theme.cardBg, border: `1px solid ${theme.borderColor}` }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: theme.borderColor }}>
            <div className="text-sm font-semibold truncate" style={{ color: theme.textPrimary }}>
              {user?.name || 'User'}
            </div>
            <div className="text-xs truncate" style={{ color: theme.textSecondary }}>{user?.email}</div>
          </div>

          <div className="p-2">
            {!isPro && (
              <button data-testid="user-menu-upgrade" onClick={() => { setOpen(false); navigate('/subscribe'); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ background: `linear-gradient(135deg, ${theme.primary}15, ${theme.accent}15)`, color: theme.primary }}>
                <Crown className="w-4 h-4" /> Upgrade to Pro
              </button>
            )}
            {isPro && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                style={{ background: `${theme.primary}08`, color: theme.primary }}>
                <Crown className="w-4 h-4" />
                <span className="font-medium">Pro Member</span>
              </div>
            )}
          </div>

          <div className="p-2 border-t" style={{ borderColor: theme.borderColor }}>
            <button data-testid="user-menu-referral" onClick={() => { setOpen(false); navigate('/referral'); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ color: theme.primary }}>
              <Gift className="w-4 h-4" /> Invite Friends
            </button>
            <button data-testid="user-menu-logout" onClick={() => { setOpen(false); logout(); navigate('/'); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ color: theme.danger || '#ef4444' }}>
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
